import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import { platform, title, logo } from './const'
import setupI18n from './i18n'
import { createVuestic } from 'vuestic-ui'
import { createGtm } from '@gtm-support/vue-gtm'
import router from './router'
import vuesticGlobalConfig from './services/vuestic-ui/global-config'
import { isChunkLoadingError, recoverFromChunkError } from './utils/chunkRecovery'
import './scss/main.scss'
import { socketService } from './services/chatsocket' 

const ICON_FONTS_MAX_WAIT_MS = 3500

const waitForMs = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

const initializeBootLoaderLogo = () => {
  const bootLogo = document.getElementById('boot-logo') as HTMLImageElement | null
  if (!bootLogo) return

  bootLogo.src = logo || '/xo-blanche.png'
  bootLogo.alt = `${title || 'Application'} logo`
  bootLogo.onerror = () => {
    bootLogo.src = '/xo-blanche.png'
  }
}

const waitForIconFonts = async () => {
  if (!('fonts' in document) || !document.fonts?.load) return

  const fontsReady = Promise.all([
    document.fonts.load('24px "Material Icons"'),
    document.fonts.load('24px "Material Symbols Outlined"'),
  ])

  await Promise.race([fontsReady, waitForMs(ICON_FONTS_MAX_WAIT_MS)])
}

const hideBootLoaderAndRevealApp = () => {
  const bootLoader = document.getElementById('boot-loader')
  bootLoader?.remove()
}
const initializeSocket = async () => {
  try {
    const getUserIdFromStorage = (): string => {
      const token = localStorage.getItem('admin_token') || 
                    sessionStorage.getItem('admin_token') || 
                    sessionStorage.getItem('impersonation_token')
      
      if (!token) return ''
      
      try {
        const parts = token.split('.')
        if (parts.length !== 3) return ''
        
        const payload = parts[1]
        const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4)
        const decodedPayload = atob(paddedPayload)
        const decoded = JSON.parse(decodedPayload)
        
        return decoded.id || decoded.userId || decoded.user_id || decoded.sub || ''
      } catch (error) {
        console.error('Erreur décodage JWT:', error)
        return ''
      }
    }

    const userId = getUserIdFromStorage()
    
    if (userId) {
      console.log('Initialisation du socket pour l\'utilisateur:', userId)
      const socket = await socketService.connect(userId)
      if (socket) {
        console.log(' Socket initialisé avec succès au démarrage')
      } else {
        console.warn('Échec de connexion du socket au démarrage')
      }
    } else {
      console.log('Authentification non trouvée, le socket sera initialisé plus tard')
    }
  } catch (error) {
    console.error('Erreur lors de l\'initialisation du socket:', error)
  }
}
const bootstrap = async () => {
  initializeBootLoaderLogo()
  // Chargement des polices en arrière-plan — l'app démarre sans attendre le délai max
  void waitForIconFonts()

  //
  // --------------------
  // CREATE APP & PINIA
  // --------------------
  const app = createApp(App)
  const pinia = createPinia()
  app.use(pinia)

  const i18n = await setupI18n()
  app.use(router)
  app.use(i18n)
  app.use(createVuestic({ config: vuesticGlobalConfig }))

  if (import.meta.env.VITE_APP_GTM_ENABLED) {
    app.use(
      createGtm({
        id: import.meta.env.VITE_APP_GTM_KEY,
        debug: false,
        vueRouter: router,
      }),
    )
  }

  window.addEventListener('error', (event) => {
    const maybeError = event.error || new Error(event.message || '')
    if (isChunkLoadingError(maybeError)) {
      recoverFromChunkError()
    }
  })

  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadingError(event.reason)) {
      recoverFromChunkError()
    }
  })

  await router.isReady()
  app.mount('#app')
  hideBootLoaderAndRevealApp()
    await initializeSocket()

  // Expose `platform` for other scripts and update favicon accordingly
  ;(window as any).__PLATFORM = platform

  // Set document title based on env
  try {
    document.title = title
  } catch (e) {
    // ignore
  }

  ;(function() {
    try {
      // Keep browser tab icon aligned with env-configured brand logo.
      const faviconPath = logo 
      let link = document.getElementById('favicon') as HTMLLinkElement | null
      if (!link) {
        link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null
      }
      if (link) {
        link.href = faviconPath
      } else {
        const l = document.createElement('link')
        l.id = 'favicon'
        l.rel = 'icon'
        l.href = faviconPath
        document.head.appendChild(l)
      }
    } catch (e) {
      // silent
    }
  })()
}

bootstrap()
// const wsStore = useWebSocketStore()

// function getModalContent() {
//   const title = `
//     <strong class="text-red-600 text-lg">
//       ⚠ WebSocket Not Supported
//     </strong>
//   `
//   const htmlContent = `
//     <div class="text-gray-700 text-sm space-y-2">
//       <p>Your browser does not support WebSocket or the connection failed.</p>
//       <p>The system will automatically switch to an <b>HTTP fallback</b>.</p>
//       <p>For better performance, please use a modern and up-to-date browser.</p>
//     </div>
//   `
//   return { title, htmlContent }
// }

// // Anti-spam modal
// watch(
//   () => wsStore.isWebSocketSupported,
//   (newVal) => {
//     if (!newVal && !wsStore.modalAlreadyShown) {
//       const { title, htmlContent } = getModalContent()
//       Swal.fire({
//         title,
//         html: htmlContent,
//         icon: 'warning',
//         showCloseButton: false,
//         focusConfirm: false,
//         confirmButtonText: `<i class="fa fa-thumbs-up"></i> Continue`,
//         confirmButtonAriaLabel: 'OK',
//       }).finally(() => {
//         wsStore.markModalAsShown()
//       })
//     }
//   },
// )  { immediate: true },
